import { DragDropProvider } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { FLOW_ACTION_LABELS, moveArrayItem, type FlowAction } from "@/modules/flow";
import { useTagsQuery } from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import { TagSelector } from "@/modules/mine/components/tag-selector";
import { Button } from "@/shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";

interface ActionEditorProps {
  actions: FlowAction[];
  errors?: Record<number, string>;
  onChange: (actions: FlowAction[]) => void;
}

interface SortableActionProps {
  action: FlowAction;
  error?: string;
  id: string;
  index: number;
  total: number;
  tagsById: Map<string, { id: string; name: string }>;
  onDelete: () => void;
  onUpdate: (action: FlowAction) => void;
}

let actionId = 0;

function createActionId() {
  actionId += 1;
  return `flow-action-${actionId}`;
}

function SortableAction({
  action,
  error,
  id,
  index,
  total,
  tagsById,
  onDelete,
  onUpdate,
}: SortableActionProps) {
  const sortable = useSortable({ id, index });

  return (
    <div
      className={`transition-opacity ${
        sortable.isDragging ? "relative z-10 opacity-95" : ""
      }`}
      data-flow-error={Boolean(error)}
      ref={sortable.ref}
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <button
          aria-label="拖动调整动作顺序"
          className="hidden h-9 touch-none cursor-grab items-center text-muted-foreground active:cursor-grabbing md:flex"
          ref={sortable.handleRef}
          type="button"
        >
          <GripVertical className="size-4" />
        </button>

        <Select
          value={action.type}
          onValueChange={(value) =>
            onUpdate(
              value === "add_tags"
                ? { type: "add_tags", tag_ids: [] }
                : { type: "move_to_folder", folder_id: "" },
            )
          }
        >
          <SelectTrigger className="w-full md:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(FLOW_ACTION_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="min-h-9 flex-1">
          {action.type === "move_to_folder" ? (
            <FolderSelector
              allowCreate
              className="w-full"
              onChange={(folderId) => onUpdate({ type: "move_to_folder", folder_id: folderId ?? "" })}
              placeholder="选择文件夹"
              portalledDropdown
              showSearch
              value={action.folder_id || null}
            />
          ) : (
            <TagSelector
              className="min-h-9"
              onChange={(tags) => onUpdate({ type: "add_tags", tag_ids: tags.map((tag) => tag.id) })}
              portalledDropdown
              triggerClassName="px-2"
              value={action.tag_ids.map((tagId) => tagsById.get(tagId) ?? { id: tagId, name: tagId })}
            />
          )}
        </div>

        <div className="flex items-center self-end md:self-auto">
          <Button
            aria-label="删除动作"
            disabled={total === 1}
            onClick={onDelete}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

export function ActionEditor({ actions, errors, onChange }: ActionEditorProps) {
  const [actionIds, setActionIds] = useState(() => actions.map(createActionId));
  const tagsQuery = useTagsQuery();
  const tagsById = useMemo(
    () => new Map((tagsQuery.data?.tags ?? []).map((tag) => [tag.id, tag])),
    [tagsQuery.data?.tags],
  );

  const moveAction = (sourceIndex: number, targetIndex: number) => {
    if (sourceIndex === targetIndex) return;
    setActionIds((ids) => moveArrayItem(ids, sourceIndex, targetIndex));
    onChange(moveArrayItem(actions, sourceIndex, targetIndex));
  };

  return (
    <div className="space-y-1">
      <DragDropProvider
        onDragEnd={(event) => {
          if (event.canceled || !isSortable(event.operation.source)) return;
          const { initialIndex, index } = event.operation.source;
          moveAction(initialIndex, index);
        }}
      >
        <div className="space-y-2 rounded-lg bg-muted/30 p-2">
          {actions.map((action, index) => (
            <SortableAction
              action={action}
              error={errors?.[index]}
              id={actionIds[index]}
              index={index}
              key={actionIds[index]}
              onDelete={() => {
                setActionIds((ids) => ids.filter((_, itemIndex) => itemIndex !== index));
                onChange(actions.filter((_, itemIndex) => itemIndex !== index));
              }}
              onUpdate={(nextAction) =>
                onChange(actions.map((item, itemIndex) => itemIndex === index ? nextAction : item))
              }
              tagsById={tagsById}
              total={actions.length}
            />
          ))}
        </div>
      </DragDropProvider>

      <Button
        className="gap-1.5 px-2 font-normal text-muted-foreground/70 hover:bg-transparent hover:text-foreground"
        onClick={() => {
          setActionIds((ids) => [...ids, createActionId()]);
          onChange([...actions, { type: "move_to_folder", folder_id: "" }]);
        }}
        size="sm"
        type="button"
        variant="ghost"
      >
        <Plus className="size-4" />
        添加动作
      </Button>
    </div>
  );
}
